/**
 * townTapGuard — the pure decision behind Town's "did this DEPTH-0 world tap REALLY
 * mean to hit the world?" guard.
 *
 * WHY THIS EXISTS (the "stuck in Town" bug — now BELT-AND-SUSPENDERS). The scene runs with
 * Phaser's DEFAULT top-only hit-testing (`scene.input.topOnly = TRUE`), so a tap is
 * dispatched only to the single top-most interactive object: a higher-depth control over a
 * walker should win the tap on its own. (ScrollList — used by Town's chat panel, the Codex,
 * the Dex — no longer flips that flag to false; it tracks its touch-drag at the scene input
 * level instead, so nothing globally re-enables pass-through hit-testing.)
 *
 * The town's trainer WALKERS and the walkable GROUND are interactive at depth 0, BENEATH
 * the menu drawer / its toggle / "Set out" / the chat panel / any modal scrim. If a lower
 * depth-0 world object ever slipped through — a stray per-object hit, or some future
 * stacking that reintroduced pass-through — a tap on one of those CONTROLS could ALSO fire
 * the depth-0 world handler, greeting a trainer (busy=true + a greet overlay) and locking
 * the player out of setting out / opening the menu / entering a building. So each depth-0
 * world handler additionally gates itself: it only acts when its object is GENUINELY the
 * top-most interactive object under the pointer.
 *
 * Each depth-0 world handler asks: "of the objects actually under this pointer, sorted
 * top-FIRST, is MY object the one on top?" Only then does it fire — a redundant guarantee
 * on top of top-only hit-testing that a tap on a higher-depth control never reaches the
 * world.
 *
 * Kept here as a pure function (no Phaser types) so it is trivially unit-testable: the
 * scene resolves the ordered hit list + the candidate object via Phaser, then asks us.
 */

/**
 * Should a DEPTH-0 world handler whose object is `target` act for this tap?
 *
 * @param topHits  The interactive objects under the pointer, sorted TOP-FIRST (the order
 *                 Phaser's own dispatch uses: `sortGameObjects(hitTestPointer(pointer))`).
 *                 The element at index 0 is the genuine top-most object; a higher-depth UI
 *                 control on top of `target` therefore precedes it in this list.
 * @param target   The object the depth-0 world handler belongs to (a walker's container for
 *                 the greet handler, the ground graphics for tap-to-move). May be undefined
 *                 (e.g. a follower has no greet target) — then the tap is never a world tap.
 *
 * @returns true ONLY when `target` is the genuine top-most hit — i.e. nothing higher-depth
 *          (a menu toggle/drawer button, Set out, a chat panel/row, a modal scrim) sits on
 *          top of it. When some OTHER object is on top, returns false so the world handler
 *          stays silent and the control's own handler is the only thing that fires.
 *
 * Edge cases (all biased toward NOT firing a world tap, so a control tap can never
 * accidentally greet/walk):
 *  - `target` undefined            -> false (no world object to act on).
 *  - empty `topHits`               -> false (defensive; a real world tap always includes
 *                                    `target`, so an empty list means we can't confirm it
 *                                    was the top — stay silent).
 *  - `target` present but not on top -> false (a higher-depth control is the real target).
 */
export function isTopWorldTap(
  topHits: readonly unknown[],
  target: unknown | undefined | null,
): boolean {
  if (target == null) return false;
  if (topHits.length === 0) return false;
  return topHits[0] === target;
}
